import {
  css as deprecatedCss,
  view as deprecatedView,
} from '@sail/ui/deprecated';
import {view, Icon} from '@sail/ui';
import {animate as animateDeprecated} from '@sail/engine/deprecated';
import {AnimatePresence, animate} from '@sail/engine';
import type {IconAsset} from '@sail/icons/types';
import {
  useNewUniversalHelpDesign,
  useUniversalHelpDotComDesign,
} from '../../FeatureFlagsProvider';
import {TextBubble} from './TextBubble';
import {ANIMATION_DURATION, useTheme} from '../../index';
import {dotComColors} from '../../theme/dotCom';

export interface Option {
  value: string;
  label: string;
  onPress?: () => void;
  icon?: IconAsset;
}

type Props = {
  selected?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  animated?: boolean;
  option: Option;
  onPress?: () => void;
};

export const ChatButtonOption = ({
  selected = false,
  disabled = false,
  hidden = false,
  animated = false,
  option,
  onPress,
}: Props): JSX.Element => {
  const useNewDesign = useNewUniversalHelpDesign();
  const useDotComDesign = useUniversalHelpDotComDesign();

  if (useNewDesign) {
    return (
      // eslint-disable-next-line no-use-before-define
      <ChatButtonOptionNext
        selected={selected}
        disabled={disabled}
        animated={animated}
        hidden={hidden}
        option={option}
        onPress={onPress}
      />
    );
  } else if (useDotComDesign) {
    return (
      // eslint-disable-next-line no-use-before-define
      <ChatButtonOptionDotCom
        selected={selected}
        disabled={disabled}
        hidden={hidden}
        option={option}
        onPress={onPress}
      />
    );
  }

  const {label, icon} = option;

  const bg = selected ? 'neutral600' : 'white';
  const border = selected ? 'neutral600' : 'primary';
  const color = selected ? deprecatedCss.value('white') : 'primary';

  let selectedContent;
  if (useNewDesign && selected) {
    selectedContent = <TextBubble body={label} messageType="user" />;
  }

  // selected options won't be hoverable, so these values are only for unselected buttons
  // at the moment, border & text color remains primary on hover, the bg will slightly darken
  const bgHover = 'neutral100';
  const borderHover = 'primary';
  const colorHover = 'primary';

  return (
    <AnimatePresence>
      {hidden
        ? null
        : selectedContent || (
            <deprecatedView.button
              // 'data-text' is used across entire universal help synthetic test suite, kindly refrain from removing this attribute
              data-text={label}
              data-testid="chat-option"
              disabled={disabled}
              css={{
                border: deprecatedCss.value('solid'),
                borderWidth: deprecatedCss.value('1px'),
                borderRadius: 'medium',
                borderColor: deprecatedCss.value(`$color.${border}`),
                background: deprecatedCss.value(`$color.${bg}`),
                paddingX: 'medium',
                paddingY: 'small',
                whiteSpace: deprecatedCss.value('pre-line'),
                overflow: deprecatedCss.value('hidden'),
                overflowWrap: deprecatedCss.value('break-word'),
                color,
                width: deprecatedCss.value('fit-content'),
                textAlign: deprecatedCss.value('start'),
                '&:hover':
                  selected || disabled || useNewDesign
                    ? {}
                    : {
                        background: deprecatedCss.value(`$color.${bgHover}`),
                        borderColor: deprecatedCss.value(
                          `$color.${borderHover}`,
                        ),
                        color: colorHover,
                      },
              }}
              onClick={onPress}
              uses={
                animated
                  ? [
                      animateDeprecated.inout({
                        duration: ANIMATION_DURATION,
                        opacity: 0,
                        easing: 'ease-in-out',
                      }),
                    ]
                  : []
              }
            >
              <view.div css={{stack: 'x', gap: 'small', alignY: 'center'}}>
                {icon && !selected ? (
                  <Icon
                    icon={icon}
                    size="xsmall"
                    data-testid="chat-button-option-icon"
                  />
                ) : null}
                {label}
              </view.div>
            </deprecatedView.button>
          )}
    </AnimatePresence>
  );
};

const ChatButtonOptionNext = ({
  selected = false,
  disabled = false,
  hidden = false,
  animated = false,
  option,
  onPress,
}: Props): React.ReactNode => {
  const {label, icon} = option;

  return (
    <AnimatePresence>
      {/* eslint-disable-next-line no-nested-ternary */}
      {hidden ? null : selected ? (
        <view.div css={{marginY: -2}}>
          <TextBubble body={label} messageType="user" />
        </view.div>
      ) : (
        <view.button
          // 'data-text' is used across entire universal help synthetic test suite, kindly refrain from removing this attribute
          data-text={label}
          className={`chat-button-option${selected ? ' selected' : ''}`}
          data-testid="chat-option"
          disabled={disabled}
          css={{
            display: 'flex',
            alignItems: 'center',
            border: 'solid',
            borderWidth: '1.5px',
            borderRadius: '16px',
            borderColor: 'transparent',
            paddingX: '11px',
            paddingY: '7px',
            font: 'label.small',
            textColor: 'subdued',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
            backgroundImage:
              'linear-gradient(white, white), linear-gradient(to right, #675DFF 0%, #55DDFF 105%)',
            transition: 'border-color 0.4s ease',
            width: 'fit-content',
            whiteSpace: 'pre-line',
            overflow: 'hidden',
            overflowWrap: 'break-word',
            textAlign: 'start',
            ':hover': {
              borderColor: 'action.primary',
              textColor: 'text',
            },
          }}
          onClick={onPress}
          uses={
            animated
              ? [
                  animate.in({
                    duration: ANIMATION_DURATION,
                    opacity: 0,
                    easing: 'ease-in-out',
                  }),
                ]
              : []
          }
        >
          {icon ? (
            <Icon
              icon={icon}
              size="xsmall"
              data-testid="chat-button-option-icon"
              css={{
                minWidth: 'space.150',
                marginRight: 'small',
              }}
            />
          ) : null}
          {label}
        </view.button>
      )}
    </AnimatePresence>
  );
};

const ChatButtonOptionDotCom = ({
  selected = false,
  disabled = false,
  hidden = false,
  option,
  onPress,
}: Omit<Props, 'animated'>): React.ReactNode => {
  const theme = useTheme();
  const {label} = option;

  if (hidden) {
    return null;
  }

  return (
    <view.button
      // 'data-text' is used across entire universal help synthetic test suite, kindly refrain from removing this attribute
      data-text={label}
      className={`chat-button-option${selected ? ' selected' : ''}`}
      data-testid="chat-option"
      disabled={disabled}
      css={{
        border: '1px solid',
        borderColor: selected ? theme.linkColor : dotComColors.brand300,
        borderRadius: 'medium',
        background: selected ? theme.linkColor : dotComColors.brand,
        paddingX: '14px',
        paddingY: 'small',
        whiteSpace: 'pre-line',
        overflow: 'hidden',
        overflowWrap: 'break-word',
        color: selected ? dotComColors.brand : dotComColors.brand600,
        width: 'fit-content',
        textAlign: 'start',
        transition: '150ms linear',
        ...(!selected && {
          ':hover': {
            color: theme.linkColor,
            borderColor: '#c4ccd8',
            boxShadow:
              '0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.06), 0 0.125rem 0.25rem 0 rgba(0, 0, 0, 0.1)',
          },
        }),
      }}
      onClick={onPress}
    >
      {label}
    </view.button>
  );
};
